import React, { useEffect, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Loader2, Heart, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail, useAuth } from "../lib/auth";
import { resolveLandingRoute } from "../lib/experienceResolver";
import BtcLogo from "../components/BtcLogo";

const SUPPORT_AREAS = [
  { v: "emotional", l: "Emotional support" },
  { v: "practical", l: "Practical everyday help" },
  { v: "information", l: "Trusted information & guidance" },
  { v: "community", l: "Community & connection" },
  { v: "financial", l: "Financial guidance" },
  { v: "carer_respite", l: "Carer respite" },
];

const AVAILABILITY = [
  { v: "weekday_mornings", l: "Weekday mornings" },
  { v: "weekday_afternoons", l: "Weekday afternoons" },
  { v: "weekday_evenings", l: "Weekday evenings" },
  { v: "weekends", l: "Weekends" },
];

const COMMS = [
  { v: "email", l: "Email" },
  { v: "chat", l: "In-portal chat" },
  { v: "phone", l: "Phone call" },
  { v: "video", l: "Video call" },
];

const SURVIVOR_STAGES = [
  { v: "recent", l: "Under 1 year post-treatment" },
  { v: "one_to_five", l: "1 to 5 years post-treatment" },
  { v: "long_term", l: "5+ years post-treatment" },
];
const CARER_STAGES = [
  { v: "supporting_active", l: "Supporting an active patient" },
  { v: "supporting_survivor", l: "Supporting a survivor" },
  { v: "grieving", l: "Have supported a loved one who has passed" },
];

function toggleIn(list, v) {
  const set = new Set(list || []);
  set.has(v) ? set.delete(v) : set.add(v);
  return Array.from(set);
}

const empty = {
  bio: "",
  motivation: "",
  journey_stage: "",
  languages: ["English"],
  location: "",
  support_areas: [],
  communication_preferences: [],
  availability: [],
  relevant_experience: "",
  safeguarding_agreement: false,
};

export default function BuddyApply() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [existing, setExisting] = useState(undefined);
  const [form, setForm] = useState(empty);
  const [submitting, setSubmitting] = useState(false);
  const [langInput, setLangInput] = useState("");

  useEffect(() => {
    if (!user || typeof user !== "object") return;
    (async () => {
      try {
        const { data } = await api.get("/buddy/application/me");
        setExisting(data);
      } catch (_e) {
        setExisting({ application: null, eligible: false });
      }
    })();
  }, [user]);

  if (loading || existing === undefined) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <Loader2 className="w-8 h-8 text-[#23803F] animate-spin" />
      </div>
    );
  }
  if (!user || typeof user !== "object") return <Navigate to="/login" replace />;
  if (!existing.eligible) {
    return (
      <div className="min-h-screen bg-[#F8F9FB] flex items-center justify-center p-6">
        <div className="max-w-lg bg-white rounded-3xl p-10 shadow-sm border border-gray-100 text-center">
          <ShieldCheck className="w-10 h-10 mx-auto text-[#23803F]" />
          <h1 className="text-2xl font-bold mt-4 text-[#0B132B]">Only survivors and carers can apply</h1>
          <p className="text-[#4B5563] mt-2">
            The Buddy programme is for people who have walked this path themselves. If you're a patient,
            complete your intake and we'll match you with a Buddy.
          </p>
          <Link to="/portal/dashboard" className="btc-btn-primary mt-6 inline-flex">
            Back to dashboard
          </Link>
        </div>
      </div>
    );
  }

  const app = existing.application;
  if (app && app.status === "pending") {
    return (
      <StatusScreen
        icon="clock"
        title="Application under review"
        body="A BTC Buddy Team member is reading your responses. You'll hear back soon."
      />
    );
  }
  if (app && app.status === "approved") {
    return (
      <StatusScreen
        icon="heart"
        title="You're an approved Buddy"
        cta={{ to: "/buddy/dashboard", label: "Open Buddy dashboard" }}
        body="Head to your Buddy dashboard to manage your profile and connection requests."
      />
    );
  }
  if (app && app.status === "rejected") {
    return (
      <StatusScreen
        icon="clock"
        title="Application not moving forward"
        body="Thanks for applying. Our team decided not to move forward at this time. You can contact support if you'd like feedback."
      />
    );
  }

  const stages = user.role === "survivor" ? SURVIVOR_STAGES : CARER_STAGES;

  const submit = async () => {
    setSubmitting(true);
    try {
      const payload = { ...form };
      if (!payload.relevant_experience) delete payload.relevant_experience;
      await api.post("/buddy/apply", payload);
      toast.success("Thank you. Your Buddy application is with our team.");
      // Route through the Experience Resolver so a newly-created
      // buddy applicant lands where the resolver dictates.
      try {
        const dest = await resolveLandingRoute();
        navigate(dest, { replace: true });
      } catch (_e) {
        toast.error("Submitted, but we could not determine where to send you. Please retry.");
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Could not submit.");
    } finally {
      setSubmitting(false);
    }
  };

  const canSubmit =
    form.bio.length >= 20 &&
    form.motivation.length >= 20 &&
    form.journey_stage &&
    form.languages.length > 0 &&
    form.location.length >= 2 &&
    form.support_areas.length > 0 &&
    form.communication_preferences.length > 0 &&
    form.availability.length > 0 &&
    form.safeguarding_agreement;

  return (
    <div className="min-h-screen bg-[#F8F9FB]" data-testid="buddy-apply-page">
      <div className="max-w-[880px] mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-14">
        <Link to="/portal/dashboard" className="inline-flex items-center gap-2 mb-6 text-[#4B5563] hover:text-[#0B132B]">
          <ArrowLeft className="w-4 h-4" /> Back
        </Link>
        <div className="mb-4">
          <BtcLogo className="h-12 w-auto" />
        </div>
        <div className="bg-white rounded-3xl shadow-sm border border-gray-100 p-6 sm:p-10">
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-[#0B132B]">Become a BTC Buddy</h1>
          <p className="text-[#4B5563] mt-3">
            Buddies walk alongside patients and carers who need someone who's been there. Tell us
            about you and how you'd like to help. Applications are reviewed by our safeguarding team.
          </p>

          <Section title="Your story">
            <TextArea
              tid="buddy-bio"
              value={form.bio}
              onChange={(v) => setForm({ ...form, bio: v })}
              placeholder="A short introduction — who you are, your journey. Min 20 characters."
              minLen={20}
            />
          </Section>

          <Section title="Why do you want to be a Buddy?">
            <TextArea
              tid="buddy-motivation"
              value={form.motivation}
              onChange={(v) => setForm({ ...form, motivation: v })}
              placeholder="Tell us what motivates you. Min 20 characters."
              minLen={20}
            />
          </Section>

          <Section title="Where in your journey are you?">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {stages.map((s) => (
                <Choice
                  key={s.v}
                  tid={`buddy-stage-${s.v}`}
                  selected={form.journey_stage === s.v}
                  onClick={() => setForm({ ...form, journey_stage: s.v })}
                  label={s.l}
                />
              ))}
            </div>
          </Section>

          <Section title="Languages you speak fluently">
            <div className="flex flex-wrap gap-2 mb-3">
              {form.languages.map((l) => (
                <span
                  key={l}
                  className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#2D9B4E]/10 text-[#23803F] text-sm font-semibold"
                >
                  {l}
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, languages: form.languages.filter((x) => x !== l) })}
                    className="text-[#23803F]/70 hover:text-red-600"
                    data-testid={`buddy-lang-remove-${l}`}
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
            <div className="flex gap-2">
              <input
                data-testid="buddy-lang-input"
                value={langInput}
                onChange={(e) => setLangInput(e.target.value)}
                placeholder="Add a language (e.g. Spanish)"
                className="flex-1 px-4 py-3 rounded-xl bg-[#F8F9FB] border border-gray-200 focus:bg-white focus:border-[#2D9B4E] focus:outline-none"
              />
              <button
                type="button"
                data-testid="buddy-lang-add"
                onClick={() => {
                  const v = langInput.trim();
                  if (v && !form.languages.includes(v)) {
                    setForm({ ...form, languages: [...form.languages, v] });
                  }
                  setLangInput("");
                }}
                className="px-4 py-3 rounded-xl bg-[#0B132B] text-white text-sm font-semibold"
              >
                Add
              </button>
            </div>
          </Section>

          <Section title="Where are you based?">
            <input
              data-testid="buddy-location"
              value={form.location}
              onChange={(e) => setForm({ ...form, location: e.target.value })}
              placeholder="City & country (e.g. London, UK)"
              className="w-full px-4 py-3.5 rounded-xl bg-[#F8F9FB] border border-gray-200 focus:bg-white focus:border-[#2D9B4E] focus:outline-none"
            />
          </Section>

          <Section title="What can you offer support with?">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {SUPPORT_AREAS.map((a) => (
                <MultiChoice
                  key={a.v}
                  tid={`buddy-area-${a.v}`}
                  active={form.support_areas.includes(a.v)}
                  onClick={() => setForm({ ...form, support_areas: toggleIn(form.support_areas, a.v) })}
                  label={a.l}
                />
              ))}
            </div>
          </Section>

          <Section title="How are you happy to communicate?">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {COMMS.map((c) => (
                <MultiChoice
                  key={c.v}
                  tid={`buddy-comm-${c.v}`}
                  active={form.communication_preferences.includes(c.v)}
                  onClick={() =>
                    setForm({
                      ...form,
                      communication_preferences: toggleIn(form.communication_preferences, c.v),
                    })
                  }
                  label={c.l}
                />
              ))}
            </div>
          </Section>

          <Section title="When are you usually free?">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {AVAILABILITY.map((a) => (
                <MultiChoice
                  key={a.v}
                  tid={`buddy-avail-${a.v}`}
                  active={form.availability.includes(a.v)}
                  onClick={() => setForm({ ...form, availability: toggleIn(form.availability, a.v) })}
                  label={a.l}
                />
              ))}
            </div>
          </Section>

          <Section title="Relevant experience (optional)">
            <TextArea
              tid="buddy-experience"
              value={form.relevant_experience}
              onChange={(v) => setForm({ ...form, relevant_experience: v })}
              placeholder="Any peer-support, mentoring, or volunteering experience."
              minLen={0}
            />
          </Section>

          <div className="mt-8 p-5 rounded-2xl border border-[#1A5FAB]/30 bg-[#1A5FAB]/5">
            <label className="flex items-start gap-3 cursor-pointer" data-testid="buddy-safeguarding">
              <input
                type="checkbox"
                checked={form.safeguarding_agreement}
                onChange={(e) => setForm({ ...form, safeguarding_agreement: e.target.checked })}
                className="mt-1 w-4 h-4 accent-[#1A5FAB]"
              />
              <span className="text-[#0B132B] leading-relaxed text-sm">
                I have read and agree to the BTC Buddy safeguarding policy. I will complete any
                onboarding checks the team asks of me and will keep members' information private.
              </span>
            </label>
          </div>

          <div className="mt-8 flex justify-end">
            <button
              type="button"
              data-testid="buddy-submit"
              onClick={submit}
              disabled={!canSubmit || submitting}
              className="btc-btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? "Submitting..." : "Submit application"}
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="mt-8">
      <h2 className="text-lg font-bold text-[#0B132B] mb-3">{title}</h2>
      {children}
    </div>
  );
}

function TextArea({ tid, value, onChange, placeholder, minLen }) {
  const short = minLen > 0 && value.length > 0 && value.length < minLen;
  return (
    <div>
      <textarea
        data-testid={tid}
        rows={4}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className={`w-full px-4 py-3.5 rounded-xl bg-[#F8F9FB] border ${
          short ? "border-orange-400" : "border-gray-200"
        } focus:bg-white focus:border-[#2D9B4E] focus:outline-none`}
      />
      {short && <div className="text-xs text-orange-600 mt-1">At least {minLen} characters.</div>}
    </div>
  );
}

function Choice({ tid, selected, onClick, label }) {
  return (
    <button
      type="button"
      data-testid={tid}
      onClick={onClick}
      className={`text-left p-4 rounded-xl border-2 transition ${
        selected ? "border-[#2D9B4E] bg-[#2D9B4E]/5" : "border-gray-200 hover:border-[#2D9B4E]/60"
      }`}
    >
      <div className="font-semibold text-[#0B132B]">{label}</div>
    </button>
  );
}

function MultiChoice({ tid, active, onClick, label }) {
  return (
    <label
      data-testid={tid}
      className={`flex items-center gap-3 p-4 rounded-xl border-2 cursor-pointer transition ${
        active ? "border-[#2D9B4E] bg-[#2D9B4E]/5" : "border-gray-200 hover:border-[#2D9B4E]/60"
      }`}
    >
      <input type="checkbox" checked={active} onChange={onClick} className="w-4 h-4 accent-[#2D9B4E]" />
      <span className="text-[#0B132B] font-medium">{label}</span>
    </label>
  );
}

function StatusScreen({ icon, title, body, cta }) {
  return (
    <div className="min-h-screen bg-[#F8F9FB] flex items-center justify-center p-6">
      <div className="max-w-lg bg-white rounded-3xl p-10 shadow-sm border border-gray-100 text-center">
        <div className="w-14 h-14 rounded-full bg-[#2D9B4E]/10 mx-auto flex items-center justify-center">
          <Heart className="w-7 h-7 text-[#23803F]" />
        </div>
        <h1 className="text-2xl font-bold mt-4 text-[#0B132B]" data-testid="buddy-app-status">{title}</h1>
        <p className="text-[#4B5563] mt-2">{body}</p>
        <div className="mt-6 flex justify-center gap-3">
          <Link to="/portal/dashboard" className="btc-btn-secondary">Dashboard</Link>
          {cta && (
            <Link to={cta.to} className="btc-btn-primary">
              {cta.label} <ArrowRight className="w-4 h-4" />
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}
